import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Logout from './auth/Logout';
import ProfileIcon from './ProfileIcon';
import Sidebar from './Sidebar';
import logo from '../assets/icons/logoWhite.png';
import { FaBars } from 'react-icons/fa6';
import { useIsLoggedIn } from '../state';

const PageLayout = ({ children, showLogout = true, showProfileIcon = false }) => {
    const navigate = useNavigate();
    const isLoggedIn = useIsLoggedIn();
    const [showSidebar, setShowSidebar] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    console.log('rendering pagelayout');
    
    // Check if the screen is mobile size
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768); // 768px is a common breakpoint for mobile
            console.log('is mobile: ', window.innerWidth < 768);
        };
        
        // Initial check
        checkIfMobile();
        
        
        // Add event listener for window resize
        window.addEventListener('resize', checkIfMobile);
        
        // Clean up
        return () => {
            window.removeEventListener('resize', checkIfMobile);
            // Make sure to remove the class when component unmounts
            document.body.classList.remove('overflow-hidden');
        };
    }, []);
    
    // Effect to handle body scroll lock and Escape key when sidebar state changes
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowSidebar(false);
            }
        };

        if (showSidebar) {
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }

        // Clean up function to ensure we remove the class and event listener if component unmounts while sidebar is open
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (showSidebar) {
                document.body.classList.remove('overflow-hidden');
            }
        };
    }, [showSidebar]);
    
    const toggleSidebar = () => {
        const newSidebarState = !showSidebar;
        setShowSidebar(newSidebarState);
        
        // Toggle body scroll lock when sidebar is open/closed
        if (newSidebarState && isMobile) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    };
    
    const handleLogoClick = (e) => {
        e.preventDefault();
        navigate('/');
    };
    
    return (
        <div className="page-background min-h-screen relative font-worksans">
            {/* Header with fixed height */}
            <header className="absolute md:fixed top-0 left-0 right-0 z-50">
                {/* Logo in top left */}
                <div>
                    <button
                        className="text-white absolute top-5 md:top-6 left-5 md:left-10"
                        onClick={handleLogoClick}
                        aria-label="Go to homepage"
                    >
                        <img src={logo} className="h-10 md:h-12 w-auto" alt="tipl logo" />
                    </button>
                </div>

                {/* Top right controls - different for mobile vs desktop */}
                {isLoggedIn && isMobile && showProfileIcon ? (
                    <div className="absolute top-4 right-4 z-100">
                        <button 
                            onClick={toggleSidebar}
                            className="flex items-center justify-center w-10 h-10 rounded-full bg-white overflow-hidden focus:outline-none focus:ring-2 focus:ring-teal-300"
                            aria-label="Open menu"
                            aria-expanded={showSidebar}
                            aria-controls="mobile-sidebar"
                        >
                            <FaBars size={20} className="text-[#68C7CC]" />
                        </button>
                    </div>
                ) : (
                    <>
                        {isLoggedIn && showLogout && !showProfileIcon && (
                            <div className="absolute top-4 right-4">
                                <Logout />
                            </div>
                        )}
                        {isLoggedIn && showProfileIcon && (
                            <div className="fixed top-4 right-4 z-50">
                                <ProfileIcon />
                            </div>
                        )}
                    </>
                )}
            </header>

            {/* Main content */}
            <main className="w-full min-h-screen pt-5 md:pt-0 pb-0">
                {/* Sidebar overlay on mobile - positioned below header */}
                {showSidebar && <div id="mobile-sidebar" className="fixed left-0 top-16 w-full h-[calc(100%-4rem)] z-40 md:hidden overflow-y-auto">
                    <Sidebar onItemClick={() => setShowSidebar(false)} />
                </div>}
                
                {/* Desktop sidebar - fixed position */}
                {showSidebar && <div className="hidden md:block">
                    <Sidebar />
                </div>}
                
                {/* Page content */}
                {children}
            </main>

            {/* Footer */}
            <footer className="absolute bottom-0 left-0 right-0 py-4 text-center text-white/80 text-sm h-12">
                <div className="container mx-auto">
                    <span>&copy; {new Date().getFullYear()} Tipl</span>
                    <span className="mx-2">·</span>
                    <Link to="/privacy" className="underline hover:text-white">Privacy</Link>
                    <span className="mx-2">·</span>
                    <Link to="/terms" className="underline hover:text-white">Terms</Link>
                    <span className="mx-2">·</span>
                    <Link to="/contact" className="underline hover:text-white">Contact</Link>
                </div>
            </footer>
        </div>
    );
};

export default PageLayout;